import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, CheckCircle2, Circle, ArrowRight } from './CustomIcons';
import { useAuth } from '../context/AuthContext';

// ── Per-role onboarding checklists with navigation paths ──────────────────────

const ROLE_CHECKLISTS = {
  learner: {
    title: 'Welcome to AssistivOS',
    subtitle: "Three steps to unlock your next chapter. Check each one off as you go.",
    steps: [
      {
        id: 'confirm_pathway',
        label: 'Confirm your pathway interest',
        explainer: 'Choose the career track that fits your goals.',
        path: '/learn',
        cta: 'Browse Pathways',
      },
      {
        id: 'first_lesson',
        label: 'Complete your first lesson',
        explainer: 'Pick up where you left off — every lesson earns credits.',
        path: '/learn',
        cta: 'Start Learning',
      },
      {
        id: 'reentry_goal',
        label: 'Set a reentry savings goal',
        explainer: 'Plan ahead for housing, transport, and reintegration costs.',
        path: '/reentry',
        cta: 'Open Reentry Plan',
      },
    ],
  },

  facility_admin: {
    title: 'Welcome, Administrator',
    subtitle: 'Get your facility set up in three quick steps.',
    steps: [
      {
        id: 'review_roster',
        label: "Review your facility's learner roster",
        explainer: "See who's enrolled, their progress, and flagged learners.",
        path: '/admin',
        cta: 'View Roster',
      },
      {
        id: 'invite_staff',
        label: 'Invite an instructor or case manager',
        explainer: 'Add staff so they can support and track learner progress.',
        path: '/admin',
        cta: 'Manage Staff',
      },
      {
        id: 'alert_thresholds',
        label: 'Set engagement alert thresholds',
        explainer: 'Configure when learners are flagged as at-risk.',
        path: '/admin',
        cta: 'Go to Analytics',
      },
    ],
  },

  case_manager: {
    title: 'Welcome, Case Manager',
    subtitle: 'Start supporting your caseload in three steps.',
    steps: [
      {
        id: 'review_caseload',
        label: 'Review your caseload',
        explainer: 'See learner progress, credentials, and reentry milestones.',
        path: '/case-manager',
        cta: 'Open Caseload',
      },
      {
        id: 'understand_at_risk',
        label: 'Understand what triggers an At-Risk flag',
        explainer: 'Flags fire if: no login in 5+ days, no progress in 7+ days, or progress < 25%.',
        path: '/case-manager',
        cta: 'See At-Risk Panel',
      },
      {
        id: 'first_checkin',
        label: 'Send your first check-in note',
        explainer: 'Click any learner card to open their profile and add a note.',
        path: '/case-manager',
        cta: 'Open First Card',
      },
    ],
  },

  instructor: {
    title: 'Welcome, Instructor',
    subtitle: 'Set up your class and start tracking student success.',
    steps: [
      {
        id: 'invite_students',
        label: 'Invite or import your first students',
        explainer: 'Your student roster populates automatically once learners are assigned to your pathways.',
        path: '/instructor',
        cta: 'View Roster',
      },
      {
        id: 'review_materials',
        label: 'Review your course materials',
        explainer: 'Browse the learning content assigned to your pathways.',
        path: '/learn',
        cta: 'Browse Courses',
      },
      {
        id: 'progress_view',
        label: 'Set up your grading / progress view',
        explainer: 'Sort and filter by progress, streak, or at-risk status.',
        path: '/instructor',
        cta: 'Open Progress View',
      },
    ],
  },

  employer_partner: {
    title: 'Welcome, Employer Partner',
    subtitle: 'Three steps to start connecting with second-chance talent.',
    steps: [
      {
        id: 'complete_profile',
        label: 'Complete your company profile',
        explainer: 'A complete profile gets seen by more job-ready candidates.',
        path: '/jobs/employer',
        cta: 'Edit Profile',
      },
      {
        id: 'learn_tags',
        label: 'Learn what "Second Chance" and "WOTC Eligible" mean',
        explainer: '"Second Chance" = open to justice-impacted candidates. "WOTC Eligible" = federal tax credit for hiring.',
        path: '/jobs/employer',
        cta: 'View Listings',
      },
      {
        id: 'first_listing',
        label: 'Post your first job listing',
        explainer: 'Listings appear immediately to qualified, credential-verified candidates.',
        path: '/jobs/employer',
        cta: 'Post a Job',
      },
    ],
  },

  provider_partner: {
    title: 'Welcome, Course Provider',
    subtitle: 'Publish your content and start reaching learners.',
    steps: [
      {
        id: 'confirm_org',
        label: 'Confirm your organization profile',
        explainer: 'Verify your name, accreditation status, and service area.',
        path: '/marketplace/provider/settings',
        cta: 'Open Settings',
      },
      {
        id: 'review_courses',
        label: 'Review your published courses',
        explainer: 'Check which courses are live and visible to learners.',
        path: '/marketplace/provider/courses',
        cta: 'View Courses',
      },
      {
        id: 'check_analytics',
        label: 'Check your enrollment analytics',
        explainer: 'See how many learners are enrolled across your catalog.',
        path: '/marketplace/provider/analytics',
        cta: 'Open Analytics',
      },
    ],
  },
};

function getRoleChecklist(role) {
  return ROLE_CHECKLISTS[role] || ROLE_CHECKLISTS.learner;
}

// ── localStorage helpers ──────────────────────────────────────────────────────

function lsKey(userId) { return `aos_onboarding_v2_${userId}`; }
function lsVisitedKey(userId) { return `aos_visited_${userId}`; }
function lsDoneKey(role, userId) { return `aos_done_${role}_${userId}`; }

function readVisited(userId) {
  try { return new Set(JSON.parse(localStorage.getItem(lsVisitedKey(userId)) || '[]')); }
  catch { return new Set(); }
}

function markVisited(userId, path) {
  try {
    const visited = readVisited(userId);
    visited.add(path);
    localStorage.setItem(lsVisitedKey(userId), JSON.stringify([...visited]));
  } catch { /* kiosk localStorage may be restricted */ }
}

function readLocalChecked(userId) {
  try { return JSON.parse(localStorage.getItem(lsKey(userId)) || '{}'); }
  catch { return {}; }
}

function writeLocalChecked(userId, checked) {
  try { localStorage.setItem(lsKey(userId), JSON.stringify(checked)); }
  catch { /* ignore */ }
}

function readLocalDone(role, userId) {
  try { return localStorage.getItem(lsDoneKey(role, userId)) === 'done'; }
  catch { return false; }
}

function writeLocalDone(role, userId) {
  try { localStorage.setItem(lsDoneKey(role, userId), 'done'); }
  catch { /* ignore */ }
}

// ── Exported hook: call this on any linked page to auto-check its step ────────

export function useMarkOnboardingPath(path) {
  const { user } = useAuth();
  useEffect(() => {
    if (!user?._id || !path) return;
    markVisited(user._id, path);
  }, [user?._id, path]); // eslint-disable-line react-hooks/exhaustive-deps
}

// ── Main WelcomeModal component ───────────────────────────────────────────────

export default function WelcomeModal() {
  const { user, authFetch, isImpersonating } = useAuth();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [checked, setChecked] = useState({});
  const [closing, setClosing] = useState(false);

  const role = user?.role;
  const userId = user?._id;
  const checklist = getRoleChecklist(role);

  // ── Determine if we should show the modal ────────────────────────────────
  useEffect(() => {
    if (!user) return;

    // During founder impersonation: only show modal once per role per session
    if (isImpersonating) {
      const sessionKey = `aos_founder_modal_${role}`;
      if (sessionStorage.getItem(sessionKey)) return; // Already shown for this role this session
    }

    // Check server-side flag first (primary source of truth)
    authFetch('/auth/onboarding-status')
      .then(res => {
        const serverDone = res.data?.onboarding_complete;
        const localDone = readLocalDone(role, userId);

        if (serverDone || localDone) {
          // Onboarding complete — just pre-populate checked state from visited paths
          const visited = readVisited(userId);
          const preChecked = {};
          checklist.steps.forEach(s => {
            if (s.path && visited.has(s.path)) preChecked[s.id] = true;
          });
          // Also restore manually-checked steps from localStorage
          const saved = readLocalChecked(userId);
          setChecked({ ...preChecked, ...saved });
          return;
        }

        // Not done — show after short delay
        const saved = readLocalChecked(userId);
        const visited = readVisited(userId);
        const preChecked = { ...saved };
        checklist.steps.forEach(s => {
          if (s.path && visited.has(s.path)) preChecked[s.id] = true;
        });
        setChecked(preChecked);
        setTimeout(() => setVisible(true), 1000);
      })
      .catch(() => {
        // Server unreachable — use localStorage only
        const localDone = readLocalDone(role, userId);
        if (!localDone) setTimeout(() => setVisible(true), 1000);
      });
  }, [user?._id, isImpersonating]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Re-check visited paths whenever modal opens ──────────────────────────
  useEffect(() => {
    if (!visible || !userId) return;
    const visited = readVisited(userId);
    setChecked(prev => {
      const next = { ...prev };
      checklist.steps.forEach(s => {
        if (s.path && visited.has(s.path)) next[s.id] = true;
      });
      return next;
    });
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleComplete = useCallback(async () => {
    setClosing(true);
    authFetch('/auth/onboarding-complete', { method: 'POST' }).catch(() => {});
    writeLocalDone(role, userId);
    writeLocalChecked(userId, checked);
    // During founder impersonation: mark this role as shown for the session
    if (isImpersonating) {
      try { sessionStorage.setItem(`aos_founder_modal_${role}`, 'shown'); } catch (e) {}
    }
    setTimeout(() => { setVisible(false); setClosing(false); }, 280);
  }, [authFetch, role, userId, checked, isImpersonating]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleStep = (id) => {
    setChecked(prev => {
      const next = { ...prev, [id]: !prev[id] };
      writeLocalChecked(userId, next);
      return next;
    });
  };

  const handleStepCta = (step) => {
    // Mark the step as checked and navigate to linked page
    setChecked(prev => {
      const next = { ...prev, [step.id]: true };
      writeLocalChecked(userId, next);
      return next;
    });
    markVisited(userId, step.path);
    if (step.path) navigate(step.path);
  };

  const checkedCount = Object.values(checked).filter(Boolean).length;
  const allChecked = checklist.steps.every(s => checked[s.id]);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-modal-title"
      data-testid="welcome-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 600,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
        backdropFilter: 'blur(6px)',
        animation: closing ? 'none' : 'fadeInFast 0.2s ease-out',
        opacity: closing ? 0 : 1,
        transition: closing ? 'opacity 0.28s ease' : 'none',
      }}
    >
      <div
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-default)',
          borderRadius: '20px',
          padding: '32px 28px',
          maxWidth: '460px',
          width: '100%',
          position: 'relative',
          boxShadow: '0 24px 64px rgba(0,0,0,0.55)',
          maxHeight: '92vh',
          overflowY: 'auto',
          animation: closing ? 'none' : 'scaleIn 0.25s ease-out',
        }}
        data-testid="welcome-modal-card"
      >
        {/* Close */}
        <button
          onClick={handleComplete}
          aria-label="Close onboarding guide"
          data-testid="welcome-modal-close"
          style={{
            position: 'absolute', top: '16px', right: '16px',
            background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
            borderRadius: '8px', width: '32px', height: '32px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: 'var(--text-muted)', transition: 'all 0.15s', minHeight: '32px',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
        >
          <X size={14} aria-hidden="true" />
        </button>

        {/* Header */}
        <div style={{ marginBottom: '24px', paddingRight: '32px' }}>
          <div style={{
            width: '46px', height: '46px', borderRadius: '13px',
            background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px',
          }}>
            <span style={{ fontSize: '24px' }} aria-hidden="true">🎯</span>
          </div>
          <h2
            id="welcome-modal-title"
            style={{
              fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '20px',
              color: 'var(--text-primary)', margin: '0 0 6px 0',
            }}
          >
            {checklist.title}
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', fontFamily: 'Inter, sans-serif', margin: 0, lineHeight: 1.5 }}>
            {checklist.subtitle}
          </p>
        </div>

        {/* Steps */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
          {checklist.steps.map((step, i) => {
            const done = !!checked[step.id];
            return (
              <div
                key={step.id}
                data-testid={`onboarding-step-${step.id}`}
                style={{
                  background: done ? 'rgba(34,197,94,0.06)' : 'var(--bg-elevated)',
                  border: `1px solid ${done ? 'rgba(34,197,94,0.22)' : 'var(--border-subtle)'}`,
                  borderRadius: '12px', padding: '14px 14px 14px 12px',
                  transition: 'all 0.18s',
                  animation: `fadeInUp 0.3s ease-out ${i * 70}ms both`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <button
                    onClick={() => toggleStep(step.id)}
                    aria-label={`Mark "${step.label}" as ${done ? 'incomplete' : 'complete'}`}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', flexShrink: 0, marginTop: '1px', minHeight: '24px' }}
                  >
                    {done
                      ? <CheckCircle2 size={20} color="#22C55E" aria-hidden="true" />
                      : <Circle size={20} color="var(--text-dim)" aria-hidden="true" />
                    }
                  </button>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '14px',
                      color: done ? 'var(--text-muted)' : 'var(--text-primary)',
                      textDecoration: done ? 'line-through' : 'none', margin: '0 0 3px 0', transition: 'color 0.15s',
                    }}>
                      {step.label}
                    </p>
                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'Inter, sans-serif', margin: '0 0 8px 0', lineHeight: 1.4 }}>
                      {step.explainer}
                    </p>
                    {step.path && !done && (
                      <button
                        onClick={() => handleStepCta(step)}
                        data-testid={`onboarding-cta-${step.id}`}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                          background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)',
                          color: '#F97316', borderRadius: '7px', padding: '5px 10px',
                          fontSize: '12px', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600,
                          cursor: 'pointer', transition: 'background 0.15s', minHeight: '30px',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(249,115,22,0.15)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(249,115,22,0.08)'}
                      >
                        {step.cta} <ArrowRight size={11} aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Progress */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'Inter, sans-serif' }}>Progress</span>
            <span style={{ fontSize: '12px', color: '#F97316', fontWeight: 600, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {checkedCount}/{checklist.steps.length}
            </span>
          </div>
          <div style={{ height: '5px', background: 'var(--bg-elevated)', borderRadius: '3px', overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${(checkedCount / checklist.steps.length) * 100}%`,
              background: allChecked ? '#22C55E' : '#F97316',
              borderRadius: '3px', transition: 'width 0.35s ease, background 0.3s',
            }} />
          </div>
        </div>

        {/* CTA */}
        <button
          onClick={handleComplete}
          data-testid="welcome-modal-done-btn"
          style={{
            width: '100%',
            background: allChecked ? '#22C55E' : '#F97316',
            color: '#111111', border: 'none', borderRadius: '11px',
            padding: '13px 24px', fontFamily: 'Plus Jakarta Sans, sans-serif',
            fontWeight: 700, fontSize: '15px', cursor: 'pointer',
            transition: 'background 0.2s, transform 0.12s', minHeight: '44px',
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
        >
          {allChecked ? "All set — let's go!" : "Get started →"}
        </button>

        <p style={{ textAlign: 'center', fontSize: '11px', color: 'var(--text-dim)', marginTop: '10px', fontFamily: 'Inter, sans-serif' }}>
          Replay this guide anytime from Help &amp; Settings.
        </p>
      </div>
    </div>
  );
}
